import { Application } from '../domain/Application'
import { DateTime } from '../domain/DateTime'
import { DefaultOperation } from '../domain/Operation'
import { Organization } from '../domain/Organization'
import { PercentileType } from '../domain/ReportCriteria'
import Router from '../router'
import RangeModel from './timeRange'

export interface ContextRoute {
    route: string
    parameters: {
        [key: string]: string
    }
    query?: {
        [key: string]: string
    }
    urlParameters?: {
        [key: string]: string
    }
}

interface Navigation {
    extra: Array<{ url: string; icon: string | null; label: string }>
    navigationContext: 'user-settings' | 'organization-settings' | 'project-settings' | 'unknown'
    statusPage: {
        isError: boolean
        label: string
    }
    productUpdate?: {
        id: number
        title: string
        url: string
    }
}

export interface IdeIntegration {
    ide: string
    projectName: string
    localPath: string
}

export class TidewaysContext {
    public application: Application
    public customAnnotations: string[]
    public productionEnvironments: string[]
    public environment: string
    public isAdmin: boolean
    public organization: Organization
    public route: ContextRoute
    public router: Router
    public service: string
    public services: { total: number; serviceIds: { [p: string]: number } }
    public range: RangeModel
    public environments: string[]
    public operation: DefaultOperation | null
    public selectedDateRange: { startDate: string; endDate: string; minutes: number }
    public allowedToShare: boolean
    public bottlenecks: { [type: string]: { label: string; hidden: boolean } }
    public selectedEnvironment: {
        id: number
        name: string
        serverIds: number[]
        isActive: boolean
        activeUntil: DateTime
        isProduction: boolean
        extraTracesPerMinute: number
    }
    public selectedService: string
    public isElevated: boolean | null
    public percentile: PercentileType
    public navigation: Navigation
    public currentIdeIntegration: IdeIntegration
    public defaultOrganizationName: string

    constructor(contextData: any) {
        this.application = contextData.application
        this.customAnnotations = contextData.customAnnotations
        this.productionEnvironments = contextData.productionEnvironments
        this.environment = contextData.environment ? contextData.environment : 'production'
        this.isAdmin = contextData.isAdmin
        this.allowedToShare = contextData.allowedToShare
        this.organization = contextData.organization
        this.navigation = contextData.navigation
        this.route = contextData.route ? contextData.route : { parameters: {} }
        this.router = contextData.router
        this.service = contextData.service ? contextData.service : 'web'
        this.services = contextData.services
        this.range = contextData.selectedDateRange
            ? RangeModel.fromStartAndEnd(contextData.selectedDateRange.startDate + '+0000', contextData.selectedDateRange.endDate + '+0000')
            : new RangeModel()
        this.environments = contextData.environments
        this.operation = contextData.operation ? contextData.operation : null
        this.selectedDateRange = contextData.selectedDateRange
        this.selectedEnvironment = contextData.selectedEnvironment
        this.selectedService = contextData.selectedService
        this.isElevated = contextData.isElevated ? contextData.isElevated : null
        this.percentile = contextData.percentile ? contextData.percentile : '95p'
        this.currentIdeIntegration = contextData.currentIdeIntegration
        this.bottlenecks = contextData.bottlenecks ?? {}
        this.defaultOrganizationName = contextData.defaultOrganizationName ?? ''
    }

    public setRange = (range: RangeModel): void => {
        this.range = range
    }

    public getRange = (): RangeModel => this.range

    /** @deprecated Please use WithEnvironmentAndService instead */
    public setService = (service: string): void => {
        this.service = service || 'web'
    }

    /** @deprecated Please use WithEnvironmentAndService instead */
    public getService = (): string => this.service

    /** @deprecated Please use WithEnvironmentAndService instead */
    public setEnvironment = (environment: string): void => {
        this.environment = environment || 'production'
    }

    /** @deprecated Please use WithEnvironmentAndService instead */
    public getEnvironment = (): string => this.environment

    /** @deprecated Please use WithEnvironmentAndService instead */
    public isProductionEnvironment = (): boolean => {
        return this.productionEnvironments.indexOf(this.environment) !== -1
    }

    public isProfilingSpace = (): boolean => this.application && isProfilingSpace(this.application)

    /** @deprecated: use isFeatureEnabled() instead
     * this function can not properly filter against user-features, since
     * the router is always undefined in the context object, but instead holds data in the redux store
     **/
    public hasFeature = (feature: string): boolean => {
        const user = this.router?.user
        const organization = this.organization

        if (!user && !organization) {
            return false
        }

        if (user?.features?.includes(feature)) {
            return true
        }

        if (organization?.features?.includes(feature)) {
            return true
        }

        return false
    }

    public fromParameters = (parameters: { [p: string]: string | number | string[] | number[] }): void => {
        this.setRange(RangeModel.fromParameters(parameters))
        this.setService(parameters.s as string)
        this.setEnvironment(parameters.env as string)
    }

    public toParameters = (): { ts: string | null; m: number; env?: string; s?: string } => ({
        ...this.range.toParameters(),
        s: this.service,
        env: this.environment,
    })

    public getLocalStorageKey = (): string => {
        let key = 'tw'
        if (this.application) {
            key += ':' + this.application.name
        }
        if (this.organization) {
            key += ':' + this.organization.name
        }
        key += ':store'

        return key
    }
}

export const isProfilingSpace = (application: Application): boolean => application.license.license === 'profiler'
